import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Spinner from "@/v2-ui/spinner"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Placeholder from "@/v2-ui/Placeholder"
import { TooltipLink } from "@/v2-backoffice-client/components/link"
import LayerCountDriftBlock from "@/v2-backoffice-client/layer/drift/LayerCountDriftBlock"
import LayerDriftSummaryBadges from "@/v2-backoffice-client/layer/drift/LayerDriftSummaryBadges"
import LayerDriftTable from "@/v2-backoffice-client/layer/drift/LayerDriftTable"
import { useLayerDriftQuery } from "@/v2-backoffice-client/layer/drift/layer.drift.query"
import {
  getLayerDriftSheetHref,
  pluralChanges,
} from "@/v2-backoffice-client/layer/drift/layer.drift.utils"
import { countUnresolvedBreaking } from "@/v2-backoffice-client/layer/drift/resolve/layer.drift.resolve"
import { MAP_LAYER_MONITOR_SOURCE } from "@/v2-backoffice-client/layer/layer.constants"
import { formatRelativeTime } from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

export type UptimeViewDriftSectionProps = ComponentProps<"section"> & {
  target: UptimeTargetFragment
}

/**
 * Drift on the uptime sheet: whether the upstream shape moved, how badly,
 * and since when — then a door into the layer configurator.
 *
 * It used to embed the whole drift detail, entry table and accept-baseline
 * action included, which made the uptime sheet a second place to resolve
 * drift while being a worse one: no documents, no preview map, no editor,
 * so the only action an operator could actually complete here was accepting
 * the baseline — the one that silences the alarm without fixing anything.
 * Monitoring's job is to say something changed. Adapting the layer to the
 * change is the configurator's, where the layer's own documents are.
 *
 * Feature-count drift keeps its stat block (three numbers, no editing to
 * be done) but loses its accept action for the same reason.
 */
function UptimeViewDriftSection(props: UptimeViewDriftSectionProps) {
  const { target, className, ...sectionProps } = props

  const { data, loading, error } = useLayerDriftQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        source: target.Source,
        identifier: target.Identifier,
      },
    },
  })

  const detail = data?.getLayerDriftDetail ?? null
  const countDrift = detail?.CountDrift ?? null

  // No fingerprint, no count snapshot, and no flagged drift on the
  // target row -- this target type doesn't participate in any drift
  // tracking, so skip the section entirely rather than show a
  // permanent "no drift" line that doesn't add information.
  //
  // The OR also ensures targets that participate in feature-count
  // drift but have no metadata fingerprint (or vice versa) still
  // show up.
  const hasAnyDrift = target.HasMetadataDrift || target.HasCountDrift
  if (!loading && !detail && !hasAnyDrift) {
    return null
  }

  // Only map layers have a configurator to hand off to. Any other source
  // that grows fingerprints keeps the full table here, because for those
  // this sheet is the only place the drift is visible at all.
  const canHandOff = target.Source === MAP_LAYER_MONITOR_SOURCE
  const unresolvedCount = detail ? countUnresolvedBreaking(detail.Entries) : 0

  return (
    <section className={cn("flex flex-col gap-3", className)} {...sectionProps}>
      <div className="uptime-view-section-header">
        <div className="uptime-view-section-title">Configuration drift</div>
        <div className="uptime-view-section-aside">
          metadata + feature counts vs accepted baseline
        </div>
      </div>

      {loading && !detail && (
        <Spinner className="m-auto relative" displaySize="sm">
          Checking for layer-server changes...
        </Spinner>
      )}

      {error && <ErrorMessage error={error} />}

      {!loading && !error && !detail && (
        <Placeholder className="py-6">
          No fingerprint has been captured for this target yet. The next probe
          run will record one.
        </Placeholder>
      )}

      {detail && (
        <>
          <header className="flex flex-wrap items-start justify-between gap-3 rounded-md border bg-card/40 px-4 py-3">
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Icon
                  src="swap"
                  className={cn(
                    detail.HasDrift
                      ? "text-violet-500"
                      : "text-muted-foreground",
                  )}
                />
                {detail.HasDrift ? (
                  <span className="font-medium">
                    The layer server's metadata has changed (
                    {pluralChanges(detail.Entries.length)})
                  </span>
                ) : (
                  <span className="font-medium text-muted-foreground">
                    Metadata matches the accepted baseline
                  </span>
                )}
              </div>

              <div className="text-xs text-muted-foreground space-x-3">
                <span>
                  Baseline:{" "}
                  {detail.BaselineFirstAt
                    ? formatRelativeTime(detail.BaselineFirstAt)
                    : detail.BaselineAt
                      ? formatRelativeTime(detail.BaselineAt)
                      : "—"}
                </span>
                {detail.HasDrift && detail.CurrentFirstAt && (
                  <span className="text-violet-500 font-medium">
                    Drifted: {formatRelativeTime(detail.CurrentFirstAt)}
                  </span>
                )}
                <span>
                  Last seen:{" "}
                  {detail.CurrentAt
                    ? formatRelativeTime(detail.CurrentAt)
                    : "—"}
                </span>
              </div>

              {/* Counts only when this stays a summary. When the entry list
                  renders below instead of handing off, its severity sections
                  already state the same three numbers. */}
              {canHandOff && <LayerDriftSummaryBadges detail={detail} />}

              {unresolvedCount > 0 && (
                <p className="pt-0.5 text-xs text-destructive">
                  {unresolvedCount === 1
                    ? "1 breaking change is"
                    : `${unresolvedCount} breaking changes are`}{" "}
                  still referenced by the layer's own configuration.
                </p>
              )}
            </div>

            {detail.HasDrift && canHandOff && (
              <TooltipLink
                to={getLayerDriftSheetHref(target.Identifier)}
                variant={unresolvedCount > 0 ? "primary" : "secondary"}
                size="sm"
                iconSrc="pen-swirl"
                data-feature-code="UI_BackofficeUptime_Drift_Resolve"
                content="Open the layer configurator's Drift tab, where the changes can be reviewed against the layer's own documents and fixed"
              >
                Review in layer
              </TooltipLink>
            )}
          </header>

          {detail.HasDrift && !canHandOff && (
            <LayerDriftTable entries={detail.Entries} />
          )}

          {countDrift && (
            <LayerCountDriftBlock
              countDrift={countDrift}
              enableAccept={false}
            />
          )}
        </>
      )}
    </section>
  )
}

export default UptimeViewDriftSection
